import React, { useEffect, useMemo, useState } from 'react';
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface DragDropQuestionProps {
    options: string[];
    answered: boolean;
    submitting: boolean;
    resultCorrect: boolean;
    onSubmit: (order: string[]) => void;
}

function shuffleItems(options: string[]) {
    const arr = [...options];
    for (let i = arr.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

function SortableItem({
    id,
    text,
    disabled,
    showResult,
}: {
    id: string;
    text: string;
    disabled: boolean;
    showResult: boolean;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            {...attributes}
            {...(!disabled ? listeners : {})}
            className={cn(
                'flex items-center gap-4 border px-5 py-5 transition-colors',
                disabled
                    ? 'border-[#2a2a2a] bg-[#151515]'
                    : isDragging
                        ? 'cursor-grabbing border-[#8cacff] bg-[#1a2236] shadow-[0_12px_28px_rgba(140,172,255,0.16)]'
                        : 'cursor-grab border-[#242424] bg-[#151515] hover:border-[#8cacff]/45 hover:bg-[#181c28]',
            )}
        >
            <span
                className={cn(
                    "flex h-10 w-10 items-center justify-center border font-['IBM_Plex_Mono'] text-sm uppercase tracking-[0.12em]",
                    showResult ? 'border-[#00cffc] bg-[#001b20] text-[#00cffc]' : 'border-[#2b2b2b] bg-[#111111] text-[#8cacff]',
                )}
            >
                {showResult ? '✓' : '↕'}
            </span>
            <span className="flex-1 font-['Space_Grotesk'] text-lg font-bold uppercase text-white">{text}</span>
        </div>
    );
}

export default function DragDropQuestion({
    options,
    answered,
    submitting,
    resultCorrect,
    onSubmit,
}: DragDropQuestionProps) {
    const shuffledItems = useMemo(() => shuffleItems(options ?? []), [options]);
    const [items, setItems] = useState<string[]>(shuffledItems);

    useEffect(() => {
        setItems(shuffledItems);
    }, [shuffledItems]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setItems((prev) => {
            const oldIndex = prev.indexOf(active.id as string);
            const newIndex = prev.indexOf(over.id as string);
            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    const handleSubmit = () => {
        if (answered || submitting) return;
        onSubmit(items);
    };

    if (!options?.length) return null;

    return (
        <div className="space-y-5">
            {!answered ? (
                <>
                    <div className="border border-[#2d3554] bg-[#111723] px-4 py-4">
                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#8cacff]">
                            Sequence Directive
                        </p>
                        <p className="mt-3 text-sm leading-relaxed text-[#cfd8ff]">
                            Drag each item into the correct execution order, then lock the stack.
                        </p>
                    </div>

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={items} strategy={verticalListSortingStrategy}>
                            <div className="space-y-3">
                                {items.map((item) => (
                                    <SortableItem key={item} id={item} text={item} disabled={answered} showResult={false} />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>

                    <motion.button
                        whileTap={{ scale: 0.985 }}
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="inline-flex w-full items-center justify-center border border-[#8cacff] bg-[#1a2236] px-6 py-5 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.22em] text-white transition-colors hover:bg-[#222c49] disabled:cursor-not-allowed disabled:border-[#2a2a2a] disabled:bg-[#161616] disabled:text-[#767575]"
                    >
                        {submitting ? 'Validating...' : 'Lock Sequence'}
                    </motion.button>
                </>
            ) : (
                <div className="space-y-3">
                    <div className="border border-[#2d3554] bg-[#111723] px-4 py-4">
                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#8cacff]">
                            Validated Sequence
                        </p>
                        <p className="mt-3 text-sm leading-relaxed text-[#cfd8ff]">
                            {resultCorrect ? 'Correct order confirmed.' : 'Reference order restored below.'}
                        </p>
                    </div>
                    {options.map((item) => (
                        <SortableItem key={item} id={item} text={item} disabled showResult />
                    ))}
                </div>
            )}
        </div>
    );
}
